import { ReactNode } from 'react';
import { Control, FieldPath, FieldValues } from 'react-hook-form';
import { faGift, faHandsHoldingHeart, faHeart, faTicket } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormColorPicker } from './FormColorPicker';
import { FormSelect } from './FormSelect';
import { FormSlider } from './FormSlider';
import { FormSwitch } from './FormSwitch';
import { FormTextInput } from './FormTextInput';
import { FormTextarea } from './FormTextarea';

const ICON_FIELD_OPTIONS: Record<string, { label: string; icon?: ReactNode }> = {
  heart: {
    label: 'Heart',
    icon: <FontAwesomeIcon icon={faHeart} className="size-4 text-gray-500" />,
  },
  gift: {
    label: 'Gift',
    icon: <FontAwesomeIcon icon={faGift} className="size-4 text-gray-500" />,
  },
  giving_hands: {
    label: 'Giving hands',
    icon: <FontAwesomeIcon icon={faHandsHoldingHeart} className="size-4 text-gray-500" />,
  },
  ticket: {
    label: 'Ticket',
    icon: <FontAwesomeIcon icon={faTicket} className="size-4 text-gray-500" />,
  },
  none: { label: 'No icon' },
};

export interface FormSchemaEntry {
  group?: string;
  title?: string;
  placeholder?: string;
  type:
    | 'text'
    | 'textarea'
    | 'number'
    | 'select'
    | 'color'
    | 'checkbox'
    | 'switch'
    | 'slider'
    | string;
  default?: unknown;
  value?: unknown;
  options?: Record<string, string> | string[];
  min?: number;
  max?: number;
  step?: number;
  validate?: string[];
}

interface DynamicFieldProps<T extends FieldValues> {
  fieldKey: string;
  schema: FormSchemaEntry;
  control: Control<T>;
}

const normalizeOptions = (
  options: FormSchemaEntry['options'],
): { value: string; label: string }[] => {
  if (!options) return [];
  if (Array.isArray(options)) {
    return options.filter(opt => opt !== '').map(opt => ({ value: opt, label: opt }));
  }
  return Object.entries(options)
    .filter(([value]) => value !== '')
    .map(([value, label]) => ({ value, label: String(label) }));
};

export const DynamicField = <T extends FieldValues>({
  fieldKey,
  schema,
  control,
}: DynamicFieldProps<T>) => {
  const name = `config.${fieldKey}` as FieldPath<T>;
  const label = schema.title ?? fieldKey;

  switch (schema.type) {
    case 'text':
    case 'string':
      return (
        <FormTextInput
          name={name}
          control={control}
          label={label}
          placeholder={schema.placeholder}
        />
      );

    case 'textarea':
      return (
        <FormTextarea
          name={name}
          control={control}
          label={label}
          placeholder={schema.placeholder}
        />
      );

    case 'color':
      return <FormColorPicker name={name} control={control} label={label} />;

    case 'select': {
      const options = normalizeOptions(schema.options).map(opt =>
        fieldKey === 'icon' ? { ...opt, ...ICON_FIELD_OPTIONS[opt.value] } : opt,
      );
      return (
        <FormSelect
          name={name}
          control={control}
          label={label}
          options={options}
          placeholder={schema.placeholder}
        />
      );
    }

    case 'checkbox':
    case 'switch':
      return <FormSwitch name={name} control={control} label={label} />;

    case 'number':
    case 'slider':
      return (
        <FormSlider
          name={name}
          control={control}
          label={label}
          min={schema.min ?? 0}
          max={schema.max ?? 100}
          step={schema.step ?? 1}
        />
      );

    default:
      return (
        <FormTextInput
          name={name}
          control={control}
          label={label}
          placeholder={schema.placeholder}
        />
      );
  }
};
